import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { ServiceService } from '../../service.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-edit-leave-request',
  templateUrl: './edit-leave-request.component.html',
  styleUrls: ['./edit-leave-request.component.css']
})
export class EditLeaveRequestComponent implements OnInit {

  creationForm: FormGroup;
  loading = true;
  submitting = false;

  // ── Mode ──
  isCreateMode = false;

  // ── Employee dropdown (create mode) ──
  employeeDropdown: any[] = [];

  statusList = [
    'Pending',
    'Approved',
    'Rejected',
    'Pre approved',
  ];

  leaveTypes = [
    'Sick Leave',
    'Local Leave',
    'Vacation Leave',
    'Emergency Leave',
    'Official Trip',
    'Compassionate Leave',
  ];

  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  constructor(
    private fb: FormBuilder,
    private service: ServiceService,
    private activatedRoute: ActivatedRoute,
    private location: Location
  ) {
    this.creationForm = this.fb.group({
      _id: [null],
      employee_id: [''],
      employee_name: [''],
      department_id: [''],
      department_name: [''],
      leave_type: ['', Validators.required],
      from_date: ['', Validators.required],
      to_date: ['', Validators.required],
      status: [''],
      reason: [''],
      reject_reason: [''],
      decline_reason: [''],
      document_upload: [''],
      approved_by_id: [''],
      is_marriage_leave: [false],
    });
  }

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.queryParams['id'];
    const mode = this.activatedRoute.snapshot.queryParams['mode'];

    this.isCreateMode = mode === 'create';

    if (this.isCreateMode) {
      this.autoFillEmployee();   // ← replaces fetchEmployees()
      this.loading = false;
    } else if (id) {
      this.getDetails(id);
    } else {
      this.Toast.fire({ icon: 'error', title: 'Invalid request' });
      this.location.back();
    }
  }

  get f() { return this.creationForm.controls; }

  // ── Load employees for create mode dropdown ────────────────────────────────
  fetchEmployees(): void {
    this.service.ViewAllEmployeefilter('').subscribe({
      next: (res) => {
        this.employeeDropdown = res.data || [];
      },
      error: () => {
        this.employeeDropdown = [];
      }
    });
  }

  autoFillEmployee(): void {
    const employee_id = localStorage.getItem('admin_id') || '';
    const employee_name = localStorage.getItem('EmployeeName') || '';
    const department_id = localStorage.getItem('department_id') || '';
    const department_name = localStorage.getItem('department') || '';

    this.creationForm.patchValue({
      employee_id,
      employee_name,
      department_id,
      department_name,
    });
  }

  // ── When employee is selected in create mode ───────────────────────────────
  onEmployeeSelect(emp: any): void {
    if (!emp) {
      this.creationForm.patchValue({
        employee_id: '',
        employee_name: '',
        department_id: '',
        department_name: '',
      });
      return;
    }
    this.creationForm.patchValue({
      employee_id: emp.employee_id,
      employee_name: emp.employee_name,
      department_id: emp.department_id || '',
      department_name: emp.department_name || '',
    });
  }

  // ── File upload handler ────────────────────────────────────────────────────
  onFileChange(event: any): void {
    const file = event.target.files?.[0];
    if (file) {
      this.creationForm.patchValue({ document_upload: file.name });
    }
  }

  // ── Load details for edit mode ─────────────────────────────────────────────
  getDetails(id: string): void {
    this.service.viewLeaveRequestById(id).subscribe({
      next: (res) => {
        const d = res.data?.[0];
        if (!d) {
          this.Toast.fire({ icon: 'error', title: 'Record not found' });
          this.location.back();
          return;
        }

        this.creationForm.patchValue({
          _id: d._id,
          employee_id: d.employee_id,
          employee_name: d.employee_name,
          department_id: d.department_id,
          department_name: d.department_name,
          leave_type: d.leave_type,
          from_date: d.from_date ? d.from_date.substring(0, 10) : '',
          to_date: d.to_date ? d.to_date.substring(0, 10) : '',
          status: d.status,
          reason: d.reason,
          reject_reason: d.reject_reason,
          decline_reason: d.decline_reason,
          document_upload: d.document_upload,
          approved_by_id: d.approved_by_id,
          is_marriage_leave: d.is_marriage_leave || false,
        });

        this.loading = false;
      },
      error: () => {
        this.Toast.fire({ icon: 'error', title: 'Failed to load details' });
        this.location.back();
      }
    });
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  submit(): void {

    // ── Apply dynamic validators based on mode ──
    if (this.isCreateMode) {

      this.creationForm.get('employee_id')?.setValidators(Validators.required);
      this.creationForm.get('reason')?.setValidators(Validators.required);
      this.creationForm.get('status')?.clearValidators();
      this.creationForm.get('_id')?.clearValidators();

    } else {

      this.creationForm.get('status')?.setValidators(Validators.required);
      this.creationForm.get('employee_id')?.clearValidators();
      this.creationForm.get('reason')?.clearValidators();

      const statusVal = this.creationForm.get('status')?.value;
      const rejectCtrl = this.creationForm.get('reject_reason');
      if (statusVal === 'Rejected') {
        rejectCtrl?.setValidators(Validators.required);
      } else {
        rejectCtrl?.clearValidators();
      }
      rejectCtrl?.updateValueAndValidity();
    }

    // update validity for dynamically-set controls
    ['employee_id', 'reason', 'status', '_id'].forEach(ctrl => {
      this.creationForm.get(ctrl)?.updateValueAndValidity();
    });

    if (this.creationForm.invalid) {
      this.creationForm.markAllAsTouched();
      return;
    }

    this.submitting = true;

    // ── CREATE ──────────────────────────────────────────────────────────────
    if (this.isCreateMode) {

      const body = {
        employee_id: this.creationForm.value.employee_id,
        employee_name: this.creationForm.value.employee_name,
        department_id: this.creationForm.value.department_id,
        department_name: this.creationForm.value.department_name,
        leave_type: this.creationForm.value.leave_type,
        from_date: this.creationForm.value.from_date,
        to_date: this.creationForm.value.to_date,
        reason: this.creationForm.value.reason,
        document_upload: this.creationForm.value.document_upload || '',
        is_marriage_leave: this.creationForm.value.is_marriage_leave,
        status: 'Pending',
        date: new Date().toISOString(),
      };

      this.service.createLeaveRequest(body).subscribe({
        next: (res) => {
          this.submitting = false;
          if (res.success) {
            this.Toast.fire({ icon: 'success', title: 'Leave request created!' });
            this.location.back();
          } else {
            this.Toast.fire({ icon: 'error', title: res.message || 'Creation failed' });
          }
        },
        error: () => {
          this.submitting = false;
          this.Toast.fire({ icon: 'error', title: 'Error creating request' });
        }
      });

      // ── EDIT ────────────────────────────────────────────────────────────────
    } else {

      const body: any = {
        _id: this.creationForm.value._id,
        leave_type: this.creationForm.value.leave_type,
        from_date: this.creationForm.value.from_date,
        to_date: this.creationForm.value.to_date,
        status: this.creationForm.value.status,
        reason: this.creationForm.value.reason,
        reject_reason: this.creationForm.value.reject_reason,
        decline_reason: this.creationForm.value.decline_reason,
        is_marriage_leave: this.creationForm.value.is_marriage_leave,

        // ← if status is Pending, clear approved_by_id; otherwise set it
        approved_by_id: this.creationForm.value.status === 'Pending'
          ? null
          : localStorage.getItem('admin_id') || '',
      };

      this.service.updateLeaveRequest(body).subscribe({
        next: (res) => {
          this.submitting = false;
          if (res.success) {
            this.Toast.fire({ icon: 'success', title: 'Leave request updated!' });
            this.location.back();
          } else {
            this.Toast.fire({ icon: 'error', title: res.message || 'Update failed' });
          }
        },
        error: () => {
          this.submitting = false;
          this.Toast.fire({ icon: 'error', title: 'Error updating request' });
        }
      });
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  goBack(): void { this.location.back(); }

  getDaysPreview(): string {
    const from = this.creationForm.get('from_date')?.value;
    const to = this.creationForm.get('to_date')?.value;
    if (!from || !to) return 'Select dates';
    const diff = new Date(to).getTime() - new Date(from).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
    if (days <= 0) return 'Invalid range';
    return `${days} day${days > 1 ? 's' : ''}`;
  }

  getInitials(name: string): string {
    if (!name) return '?';
    return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  }

  getStatusBannerClass(status: string): string {
    const map: Record<string, string> = {
      'Pending': 'status-banner banner-pending',
      'Approved': 'status-banner banner-approved',
      'Rejected': 'status-banner banner-rejected',
      'Under Verification': 'status-banner banner-verification',
      'Pre approved': 'status-banner banner-preapproved',
    };
    return map[status] ?? 'status-banner banner-pending';
  }

  getStatusBadge(status: string): string {
    const map: Record<string, string> = {
      'Requested': 'le-badge--requested',
      'Approved': 'le-badge--approved',
      'Rejected': 'le-badge--rejected',
      'Under Verification': 'le-badge--underverify',
      'Pre approved': 'le-badge--preapproved',
      'Cancelled': 'le-badge--cancelled',
      'Completed': 'le-badge--completed',
    };
    return map[status] || '';
  }

  getStatusIcon(status: string): string {
    const map: Record<string, string> = {
      'Pending': 'fa-clock',
      'Approved': 'fa-check-circle',
      'Rejected': 'fa-times-circle',
      'Pre approved': 'fa-thumbs-up',
    };
    return map[status] || 'fa-circle';
  }
}